// features/workstations/dashboard-sections/mistake-log.js
// ═══════════════════════════════════════════════════════════
// MISTAKE LOG — manual reflective logging, Tier B learning
// ═══════════════════════════════════════════════════════════
// The full-reflection log. Distinct from trade.rule_breaks
// (in-the-moment flag). Here you write the description, the
// reason, the cost, and — the line that matters — the
// lesson for next time.
//
// Layout:
//   - Headline + tally (count + total cost in window)
//   - Add-mistake form (inline, collapsible)
//   - Filter chips (all + 7 kinds)
//   - Grouped list by occurred_at
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PillRow,
  PillTable,
  MetaField,
  PractitionerLens,
} from '../../../components/widgets/primitives';

const KIND_OPTIONS = [
  { key: 'rule_violation', label: 'Rule violation' },
  { key: 'bad_entry',      label: 'Bad entry' },
  { key: 'chased_trade',   label: 'Chased trade' },
  { key: 'size_mistake',   label: 'Size mistake' },
  { key: 'exit_mistake',   label: 'Exit mistake' },
  { key: 'emotional',      label: 'Emotional' },
  { key: 'other',          label: 'Other' },
];

const KIND_TONES = {
  rule_violation: 'terra',
  bad_entry:      'amber',
  chased_trade:   'terra',
  size_mistake:   'amber',
  exit_mistake:   'amber',
  emotional:      'purple',
  other:          'steel',
};

// ═══════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════
export default function MistakeLogSection({ workstation, userId }) {
  const [data, setData] = useState({ mistakes: [], tally: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [composing, setComposing] = useState(false);
  const [filterKind, setFilterKind] = useState('all');
  const [windowDays, setWindowDays] = useState(30);

  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const params = {
        workstation_id: workstation.id,
        user_id: userId,
        days: String(windowDays),
      };
      if (filterKind !== 'all') params.kind = filterKind;
      const qs = new URLSearchParams(params);
      const res = await fetch(`/api/workstations/mistakes?${qs}`, { cache: 'no-store' });
      const payload = res.ok ? await res.json() : null;
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      setData({
        mistakes: Array.isArray(payload?.mistakes) ? payload.mistakes : [],
        tally: payload?.tally || null,
      });
    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId, filterKind, windowDays]);

  useEffect(() => { load(); }, [load]);

  const create = useCallback(async (payload) => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/workstations/mistakes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id: workstation.id,
          user_id: userId,
          ...payload,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || `HTTP ${res.status}`);
        return false;
      }
      setComposing(false);
      await load();
      return true;
    } catch (e) {
      setError(e?.message || 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  }, [workstation?.id, userId, load]);

  const remove = useCallback(async (id) => {
    if (!confirm('Delete this log entry? This cannot be undone.')) return;
    setSaving(true);
    try {
      const qs = new URLSearchParams({ id, user_id: userId });
      const res = await fetch(`/api/workstations/mistakes?${qs}`, { method: 'DELETE' });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setError(j?.error || `HTTP ${res.status}`);
        return;
      }
      await load();
    } catch (e) {
      setError(e?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  }, [userId, load]);

  return (
    <section className="ws-mistakes">
      {loading && <div className="ws-mistakes-loading">Loading…</div>}

      {!loading && (
        <MistakesBody
          data={data}
          saving={saving}
          error={error}
          composing={composing}
          setComposing={setComposing}
          filterKind={filterKind}
          setFilterKind={setFilterKind}
          windowDays={windowDays}
          setWindowDays={setWindowDays}
          onCreate={create}
          onDelete={remove}
        />
      )}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// BODY
// ═══════════════════════════════════════════════════════════
function MistakesBody({
  data, saving, error,
  composing, setComposing,
  filterKind, setFilterKind,
  windowDays, setWindowDays,
  onCreate, onDelete,
}) {
  const tally = data.tally;
  const mistakes = Array.isArray(data.mistakes) ? data.mistakes : [];

  const total = tally?.total_count || 0;
  const costUsd = tally?.total_cost_usd || 0;
  const costR = tally?.total_cost_r || 0;

  const headline = total === 0
    ? 'No mistakes logged in the window.'
    : `${total} mistake${total === 1 ? '' : 's'} · ${fmtUsd(-costUsd)}${costR ? ` · ${fmtR(-costR)}` : ''}`;

  const summary = total === 0
    ? 'Log one when it happens — rule break, bad entry, chased setup, size mistake, emotional override. The reason + lesson matter more than the event.'
    : 'Tap ‘+ log mistake’ to add one. Filter by kind below. The lesson field is the point — future-you reads it.';

  return (
    <>
      <Eyebrow parts={[
        'mistake log',
        `${windowDays}d window`,
        total > 0 ? `${total} logged` : 'empty',
      ]} />

      <SerifHeadline>
        <span className={total > 0 ? 'ws-mistakes-tone-terra' : 'ws-mistakes-tone-sage'}>
          {headline}
        </span>
      </SerifHeadline>
      <SerifSummary>{summary}</SerifSummary>

      {error && <div className="ws-mistakes-error">{error}</div>}

      <div className="ws-mistakes-actions">
        <button
          type="button"
          className="ws-mistakes-btn ws-mistakes-btn-terra"
          onClick={() => setComposing(!composing)}
          disabled={saving}
        >
          {composing ? '✕ cancel' : '+ log mistake'}
        </button>

        <div className="ws-mistakes-window-chips">
          {[30, 60, 90, 180].map((d) => (
            <button
              key={d}
              type="button"
              className={`ws-mistakes-window-chip ${d === windowDays ? 'is-active' : ''}`}
              onClick={() => setWindowDays(d)}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {composing && (
        <ComposeMistake
          saving={saving}
          onSubmit={onCreate}
          onCancel={() => setComposing(false)}
        />
      )}

      {/* Tally by kind */}
      {tally && total > 0 && (
        <SectionStrip label="by kind" count={Object.values(tally.by_kind).filter(b => b.count > 0).length}>
          <PillTable>
            {KIND_OPTIONS.filter(k => tally.by_kind[k.key]?.count > 0).map((k) => {
              const bucket = tally.by_kind[k.key];
              const costStr = bucket.cost_usd > 0
                ? ` · ${fmtUsd(-bucket.cost_usd)}`
                : '';
              return (
                <PillRow
                  key={k.key}
                  keyText={k.label}
                  valueText={`${bucket.count}${costStr}`}
                  tone={KIND_TONES[k.key]}
                />
              );
            })}
          </PillTable>
        </SectionStrip>
      )}

      {/* Filter chips */}
      <div className="ws-mistakes-filter-row">
        <span className="ws-mistakes-filter-label">filter</span>
        <button
          type="button"
          className={`ws-mistakes-filter-chip ${filterKind === 'all' ? 'is-active' : ''}`}
          onClick={() => setFilterKind('all')}
        >
          all
        </button>
        {KIND_OPTIONS.map((k) => (
          <button
            key={k.key}
            type="button"
            className={`ws-mistakes-filter-chip ${filterKind === k.key ? 'is-active' : ''}`}
            onClick={() => setFilterKind(k.key)}
          >
            {k.label.toLowerCase()}
          </button>
        ))}
      </div>

      {/* Recent list */}
      {mistakes.length > 0 && (
        <SectionStrip label={`recent — ${filterKind === 'all' ? 'all kinds' : KIND_OPTIONS.find(k => k.key === filterKind)?.label || filterKind}`} count={mistakes.length}>
          <div className="ws-mistakes-list">
            {mistakes.map((m) => (
              <MistakeCard key={m.id} m={m} onDelete={onDelete} saving={saving} />
            ))}
          </div>
        </SectionStrip>
      )}

      <PractitionerLens label="how this pairs with strategy health">
        Strategy Health counts rule-break trades in the window. Mistake
        Log captures what HAPPENED and what you LEARNED — the richer
        reflection. If the same kind shows up five weeks running, that’s
        a habit, not a mistake. That’s where you decide to fix it.
      </PractitionerLens>
    </>
  );
}

// ═══════════════════════════════════════════════════════════
// MISTAKE CARD
// ═══════════════════════════════════════════════════════════
function MistakeCard({ m, onDelete, saving }) {
  const [expanded, setExpanded] = useState(false);
  const kindLabel = KIND_OPTIONS.find(k => k.key === m.kind)?.label || m.kind;
  const tone = KIND_TONES[m.kind] || 'steel';
  const costStr = m.cost_usd
    ? fmtUsd(-m.cost_usd)
    : m.cost_r
      ? fmtR(-m.cost_r)
      : null;

  return (
    <div className={`ws-mistakes-card ws-mistakes-card-${tone}`}>
      <button
        type="button"
        className="ws-mistakes-card-head"
        onClick={() => setExpanded((v) => !v)}
        title={expanded ? 'Collapse' : 'Expand'}
      >
        <div className="ws-mistakes-card-row">
          <span className={`ws-mistakes-kind ws-mistakes-kind-${tone}`}>{kindLabel}</span>
          {costStr && (
            <span className={`ws-mistakes-cost ws-mistakes-cost-${tone}`}>{costStr}</span>
          )}
          <span className="ws-mistakes-when">{fmtWhen(m.occurred_at)}</span>
        </div>
        <div className="ws-mistakes-desc">{m.description}</div>
      </button>

      {expanded && (
        <div className="ws-mistakes-drawer">
          {m.reason && (
            <MetaField label="why">{m.reason}</MetaField>
          )}
          {m.lesson && (
            <MetaField label="lesson">{m.lesson}</MetaField>
          )}
          <div className="ws-mistakes-drawer-actions">
            <button
              type="button"
              className="ws-mistakes-btn ws-mistakes-btn-terra"
              onClick={() => onDelete(m.id)}
              disabled={saving}
            >
              delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// COMPOSE FORM
// ═══════════════════════════════════════════════════════════
function ComposeMistake({ saving, onSubmit, onCancel }) {
  const [kind, setKind] = useState('rule_violation');
  const [description, setDescription] = useState('');
  const [reason, setReason] = useState('');
  const [lesson, setLesson] = useState('');
  const [costUsd, setCostUsd] = useState('');
  const [costR, setCostR] = useState('');

  const canSubmit = description.trim().length >= 3 && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const ok = await onSubmit({
      kind,
      description: description.trim(),
      reason: reason.trim() || undefined,
      lesson: lesson.trim() || undefined,
      cost_usd: costUsd === '' ? undefined : Math.abs(Number(costUsd)),
      cost_r: costR === '' ? undefined : Math.abs(Number(costR)),
    });
    if (ok) {
      setDescription('');
      setReason('');
      setLesson('');
      setCostUsd('');
      setCostR('');
      setKind('rule_violation');
    }
  };

  return (
    <form className="ws-mistakes-compose" onSubmit={submit}>
      <label className="ws-mistakes-field ws-mistakes-field-inline">
        <span className="ws-mistakes-field-label">kind</span>
        <select
          className="ws-mistakes-select"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
        >
          {KIND_OPTIONS.map((k) => (
            <option key={k.key} value={k.key}>{k.label}</option>
          ))}
        </select>
      </label>

      <label className="ws-mistakes-field">
        <span className="ws-mistakes-field-label">what happened</span>
        <textarea
          className="ws-mistakes-textarea"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="One line — specific. ‘Chased AAPL above 182 with no setup’."
          autoFocus
        />
      </label>

      <label className="ws-mistakes-field">
        <span className="ws-mistakes-field-label">why (optional)</span>
        <textarea
          className="ws-mistakes-textarea"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Honest reflection — what was the trigger? Emotion, FOMO, missed earlier setup, etc."
        />
      </label>

      <label className="ws-mistakes-field">
        <span className="ws-mistakes-field-label">lesson (the point)</span>
        <textarea
          className="ws-mistakes-textarea"
          rows={2}
          value={lesson}
          onChange={(e) => setLesson(e.target.value)}
          placeholder="What will you do differently next time this setup appears?"
        />
      </label>

      <div className="ws-mistakes-field-row">
        <label className="ws-mistakes-field-inline">
          <span className="ws-mistakes-field-label">cost ($)</span>
          <input
            type="number"
            step="50"
            min="0"
            className="ws-mistakes-input"
            value={costUsd}
            onChange={(e) => setCostUsd(e.target.value)}
            placeholder="optional"
          />
        </label>
        <label className="ws-mistakes-field-inline">
          <span className="ws-mistakes-field-label">cost (R)</span>
          <input
            type="number"
            step="0.1"
            min="0"
            className="ws-mistakes-input"
            value={costR}
            onChange={(e) => setCostR(e.target.value)}
            placeholder="optional"
          />
        </label>
      </div>

      <div className="ws-mistakes-actions">
        <button
          type="submit"
          disabled={!canSubmit}
          className="ws-mistakes-btn ws-mistakes-btn-sage"
        >
          {saving ? 'saving…' : 'log'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="ws-mistakes-btn ws-mistakes-btn-steel"
        >
          cancel
        </button>
      </div>
    </form>
  );
}

// ─── Formatters ───
function fmtUsd(n) {
  if (n == null || Number.isNaN(n) || n === 0) return '—';
  const sign = n > 0 ? '+' : '−';
  const abs = Math.abs(n);
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(0)}`;
}
function fmtR(n) {
  if (n == null || Number.isNaN(n) || n === 0) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${Number(n).toFixed(1)}R`;
}
function fmtWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) {
      const diffH = Math.floor(diffMs / 3600000);
      if (diffH === 0) return 'just now';
      return `${diffH}h ago`;
    }
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}
